import React from 'react';

// Placeholder filling shared by the translation helpers in i18n/index and by
// components that put React elements into a translated sentence. Both follow
// PHP sprintf: %s/%d take the next argument, %1$s/%2$d take that argument by
// number (so a translation can reorder them) without advancing the sequential
// counter, and %% is a literal percent sign. A missing argument renders as ''.
// Kept apart from i18n/index so tests that mock i18n/index still get the real
// interpolation.
const PLACEHOLDER = /%(?:(\d+)\$)?([sd%])/g;

// Walks the template, calling onText for literal text and onArgument with the
// argument index each placeholder refers to.
const eachPiece = (template, onText, onArgument) => {
	let next = 0;
	let last = 0;
	for (const match of template.matchAll(PLACEHOLDER)) {
		const [whole, position, type] = match;
		onText(template.slice(last, match.index));
		last = match.index + whole.length;
		if (type === '%') {
			onText(position === undefined ? '%' : whole);
		} else {
			onArgument(position === undefined ? next++ : Number(position) - 1);
		}
	}
	onText(template.slice(last));
};

// Values are stringified, not cast (%d doesn't truncate like PHP's). With no
// arguments only %% is unescaped, so the placeholders stay in the text.
export const sprintf = (template, args) => {
	if (!args.length) return String(template).replace(/%%/g, '%');
	let output = '';
	eachPiece(
		String(template),
		(text) => { output += text; },
		(index) => { output += index >= 0 && index < args.length ? String(args[index]) : ''; },
	);
	return output;
};

// Fills a translated template with values that may be React elements:
// interpolateElements(__('%1$s beat %2$s'), [<Winner />, <Loser />]).
// Returns an array to render as children: runs of text as strings, each element
// wrapped in a Fragment keyed by its position in the array (an argument used
// twice still gets two distinct keys). null, undefined and booleans render as
// nothing, the same as in JSX.
export const interpolateElements = (template, values = []) => {
	const parts = [];
	let text = '';
	const flushText = () => {
		if (text !== '') parts.push(text);
		text = '';
	};
	eachPiece(
		String(template),
		(literal) => { text += literal; },
		(index) => {
			const value = index >= 0 && index < values.length ? values[index] : '';
			if (value === null || value === undefined || typeof value === 'boolean') return;
			if (typeof value === 'object') {
				flushText();
				parts.push(React.createElement(React.Fragment, { key: `part-${parts.length}` }, value));
			} else {
				text += String(value);
			}
		},
	);
	flushText();
	return parts;
};
